import { FC } from 'react';
import { Stack } from '@mui/material';
import { Modal, TextField } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { useCreatePrompt, useUpdatePrompt } from 'store/prompts';

interface Values {
  name: string;
  prompt: string;
}

const INITIAL_VALUES: Values = {
  name: '',
  prompt: '',
};

interface Props extends ModalProps {
  promptId?: string;
  initialValues?: Values;
}

export const PromptModal: FC<Props> = ({
  closeModal,
  promptId,
  initialValues,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: initialValues ?? INITIAL_VALUES,
  });

  const { mutate: createPrompt, isLoading: isCreating } = useCreatePrompt();
  const { mutate: updatePrompt, isLoading: isUpdating } = useUpdatePrompt();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (promptId) {
      updatePrompt(
        { promptId, data: values },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            closeModal();
          },
        },
      );
    } else {
      createPrompt(values, {
        onSuccess: () => {
          predefinedToasts.success.added();
          closeModal();
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('prompts.title', { count: 1 })}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isCreating || isUpdating,
          },
        ]}
      >
        <Stack spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('common.name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="prompt"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('benefitTemplates.templateContent')}
                name={name}
                value={value}
                onChange={onChange}
                required
                rows={5}
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
